import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AssistantAvatarMood } from "@/ui/AssistantAvatar/assistantAvatarConfig";
import type { AnnieChatItem, AnnieProposalUiStatus } from "./useAnnie";

/** How long the celebratory face holds after a proposal is applied. */
export const ANNIE_DONE_MOOD_MS = 1200;
/** How long the attention face holds after new approvals arrive; the badge carries the count afterwards. */
export const ANNIE_NEEDS_YOU_MOOD_MS = 2400;
/** Badge value that renders no badge. */
const NO_BADGE = 0;

export interface AnnieMoodInput {
	items: AnnieChatItem[];
	isStreaming: boolean;
	doneActive: boolean;
	needsYouActive: boolean;
}

/** A failed apply only counts while it belongs to the latest turn; the next question clears it. */
const latestTurnHasFailedProposal = (items: AnnieChatItem[]): boolean => {
	for (let index = items.length - 1; index >= 0; index--) {
		const item = items[index];
		if (item.kind === "user") return false;
		if (item.kind === "proposal" && item.status === "failed") return true;
	}
	return false;
};

const streamingMood = (last: AnnieChatItem | undefined): AssistantAvatarMood | null => {
	if (last?.kind === "assistant") return AssistantAvatarMood.Talking;
	// A card that is already on screen is Annie waiting on the user, not Annie working.
	if (last?.kind === "proposal" || last?.kind === "clarification") return null;
	return AssistantAvatarMood.Thinking;
};

export const deriveAnnieMood = ({ items, isStreaming, doneActive, needsYouActive }: AnnieMoodInput): AssistantAvatarMood => {
	const last = items.at(-1);
	const whileStreaming = isStreaming ? streamingMood(last) : null;
	if (whileStreaming !== null) return whileStreaming;
	if (last?.kind === "error" || latestTurnHasFailedProposal(items)) return AssistantAvatarMood.Oops;
	if (doneActive) return AssistantAvatarMood.Done;
	if (needsYouActive) return AssistantAvatarMood.NeedsYou;
	return AssistantAvatarMood.Idle;
};

/** A boolean that turns on when fired and back off `durationMs` later; re-firing restarts the window. */
const useOneShotWindow = (durationMs: number): [boolean, () => void] => {
	const [active, setActive] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const fire = useCallback(() => {
		if (timerRef.current !== null) clearTimeout(timerRef.current);
		setActive(true);
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			setActive(false);
		}, durationMs);
	}, [durationMs]);

	useEffect(
		() => () => {
			if (timerRef.current !== null) clearTimeout(timerRef.current);
		},
		[],
	);

	return [active, fire];
};

const proposalStatuses = (items: AnnieChatItem[]): Map<string, AnnieProposalUiStatus> =>
	new Map(items.flatMap((item) => (item.kind === "proposal" ? [[item.id, item.status] as const] : [])));

/** Only a status change on a proposal already seen counts, so restoring a session with applied cards stays quiet. */
const hasNewlyDoneProposal = (previous: ReadonlyMap<string, AnnieProposalUiStatus>, current: ReadonlyMap<string, AnnieProposalUiStatus>): boolean =>
	[...current].some(([id, status]) => {
		const before = previous.get(id);
		return status === "done" && before !== undefined && before !== "done";
	});

const useDoneWindow = (items: AnnieChatItem[]): boolean => {
	const [doneActive, fireDone] = useOneShotWindow(ANNIE_DONE_MOOD_MS);
	const previousStatusesRef = useRef<ReadonlyMap<string, AnnieProposalUiStatus>>(new Map());

	useEffect(() => {
		const current = proposalStatuses(items);
		if (hasNewlyDoneProposal(previousStatusesRef.current, current)) fireDone();
		previousStatusesRef.current = current;
	}, [items, fireDone]);

	return doneActive;
};

/** The first loaded count, per department, is the baseline and never fires; a department switch starts a new baseline. */
const useNeedsYouWindow = (recommendationsCount: number | undefined, departmentId: string): boolean => {
	const [needsYouActive, fireNeedsYou] = useOneShotWindow(ANNIE_NEEDS_YOU_MOOD_MS);
	const baselineRef = useRef<number | undefined>(undefined);
	const baselineDepartmentRef = useRef(departmentId);

	useEffect(() => {
		if (baselineDepartmentRef.current !== departmentId) {
			baselineDepartmentRef.current = departmentId;
			baselineRef.current = undefined;
		}
		if (recommendationsCount === undefined) return;
		const previous = baselineRef.current;
		if (previous !== undefined && recommendationsCount > previous) fireNeedsYou();
		baselineRef.current = recommendationsCount;
	}, [recommendationsCount, departmentId, fireNeedsYou]);

	return needsYouActive;
};

export interface UseAnnieMoodInput {
	items: AnnieChatItem[];
	isStreaming: boolean;
	/** `useAnnieRecommendations().data?.length`: undefined while the queue has not loaded. */
	recommendationsCount: number | undefined;
	/** The department the recommendations belong to. */
	departmentId: string;
}

interface AnnieMoodState {
	mood: AssistantAvatarMood;
	/** Pending approvals to show on the avatar; NO_BADGE when none or not yet loaded. */
	badgeCount: number;
}

export const useAnnieMood = ({ items, isStreaming, recommendationsCount, departmentId }: UseAnnieMoodInput): AnnieMoodState => {
	const doneActive = useDoneWindow(items);
	const needsYouActive = useNeedsYouWindow(recommendationsCount, departmentId);
	const mood = useMemo(() => deriveAnnieMood({ items, isStreaming, doneActive, needsYouActive }), [items, isStreaming, doneActive, needsYouActive]);
	const badgeCount = recommendationsCount !== undefined && recommendationsCount > NO_BADGE ? recommendationsCount : NO_BADGE;
	return { mood, badgeCount };
};
